import { API } from "aws-amplify";

export interface CustomerUsageTokenResponse {
  token: string;
  expiresIn: number;
  expiresAt: string;
}

/**
 * Fetches a JWT token for customer usage embedding from the LLM Public API
 * Uses the /customers/{id}/usageEmbedToken endpoint which returns a JWT token
 * for accessing customer usage data without requiring API keys
 * 
 * @param projectId - The project ID (used for API key authentication)
 * @param customerId - The customer ID (URL encoded)
 * @param apiKey - The API key for authentication
 * @returns Promise with JWT token and expiration information
 */
export async function fetchCustomerUsageToken(
  projectId: string,
  customerId: string,
  apiKey: string
): Promise<CustomerUsageTokenResponse> {
  
  // URL encode the customer ID to handle email addresses and special characters
  const encodedCustomerId = encodeURIComponent(customerId.replace(projectId, ''));

  try {
    const response = await API.get("llmpublicapi", `/customers/${encodedCustomerId}/usageEmbedToken`, {
      headers: {
        "x-api-key": apiKey,
        "x-project-id": projectId,
      },
    });

    return response;
  } catch (error) {
    console.error("Error fetching customer usage token:", error);
    throw error;
  }
}

/**
 * Fetches customer usage token using the current user context
 * This is a convenience wrapper for authenticated admin portal usage
 */
export async function fetchCustomerUsageTokenWithContext(
  customerId: string,
  projectId: string,
  apiKey: string
): Promise<CustomerUsageTokenResponse | null> {
  try {
    const response = await fetchCustomerUsageToken(projectId, customerId, apiKey);
    return response;
  } catch (error) {
    console.error("Error fetching customer usage token with context:", error);
    return null;
  }
}
